import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useChat } from '../hooks/useChat';

const ChatMessages = ({ messages, isLoading, endRef }) => (
  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
    {messages.map((msg) => (
      <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-[80%] p-3 rounded-2xl ${
            msg.role === 'user'
              ? 'bg-[#FF6600] text-white rounded-br-md'
              : 'bg-white text-gray-800 shadow-md rounded-bl-md border border-gray-100'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
        </div>
      </div>
    ))}
    {isLoading && (
      <div className="flex justify-start">
        <div className="bg-white p-3 rounded-2xl shadow-md rounded-bl-md">
          <Loader2 className="w-5 h-5 animate-spin text-[#FF6600]" />
        </div>
      </div>
    )}
    <div ref={endRef} />
  </div>
);

const EndChatForm = ({ clientInfo, setClientInfo, isLoading, onSubmit, onCancel }) => (
  <div className="p-4 bg-gray-100 border-t border-gray-200">
    <p className="text-sm text-gray-600 mb-3">Antes de finalizar, ¿podrías dejarnos tus datos? (opcional)</p>
    <div className="space-y-2">
      <input
        type="text"
        placeholder="Tu nombre"
        value={clientInfo.name}
        onChange={(e) => setClientInfo({ ...clientInfo, name: e.target.value })}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6600]"
      />
      <input
        type="email"
        placeholder="Tu email"
        value={clientInfo.email}
        onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6600]"
      />
      <input
        type="tel"
        placeholder="Tu teléfono"
        value={clientInfo.phone}
        onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6600]"
      />
      <div className="flex gap-2 mt-3">
        <Button
          onClick={onSubmit}
          disabled={isLoading}
          className="flex-1 bg-[#FF6600] hover:bg-[#FF8C00] text-white text-sm"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar y Finalizar'}
        </Button>
        <Button onClick={onCancel} variant="outline" className="text-sm">
          Cancelar
        </Button>
      </div>
    </div>
  </div>
);

const ChatInput = ({ inputMessage, setInputMessage, isLoading, onSend, onShowEndForm }) => (
  <div className="p-4 bg-white border-t border-gray-200">
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={inputMessage}
        onChange={(e) => setInputMessage(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSend();
          }
        }}
        placeholder="Escribe tu mensaje..."
        data-testid="chatbot-input"
        disabled={isLoading}
        className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:border-[#FF6600] text-sm"
      />
      <button
        onClick={onSend}
        disabled={isLoading || !inputMessage.trim()}
        data-testid="chatbot-send-btn"
        className="w-12 h-12 rounded-full bg-[#FF6600] hover:bg-[#FF8C00] disabled:bg-gray-300 text-white flex items-center justify-center transition-colors"
      >
        <Send className="w-5 h-5" />
      </button>
    </div>
    <div className="mt-2 flex justify-center">
      <button
        onClick={onShowEndForm}
        data-testid="chatbot-end-btn"
        className="text-xs text-gray-500 hover:text-[#FF6600] transition-colors"
      >
        Finalizar conversación
      </button>
    </div>
  </div>
);

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [inputMessage, setInputMessage] = useState('');
  const [showEndForm, setShowEndForm] = useState(false);
  const messagesEndRef = useRef(null);
  const { messages, isLoading, clientInfo, setClientInfo, addGreeting, sendMessage, endChat } = useChat();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) addGreeting();
  }, [isOpen, addGreeting]);

  const handleSend = () => {
    if (!inputMessage.trim() || isLoading) return;
    const text = inputMessage;
    setInputMessage('');
    sendMessage(text);
  };

  const handleEndChat = async () => {
    const ok = await endChat();
    if (ok) setShowEndForm(false);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        data-testid="chatbot-toggle-btn"
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-[#FF6600] hover:bg-[#FF8C00] text-white shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${isOpen ? 'hidden' : ''}`}
      >
        <MessageCircle className="w-8 h-8" />
      </button>

      {isOpen && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-6 right-6 z-50 w-[380px] h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
        >
          <div className="bg-gradient-to-r from-[#1a1a1a] to-[#2d2d2d] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF6600] flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm">Atención a clientes</h3>
                <p className="text-[#FF6600] text-xs font-medium">A toda hora</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="chatbot-close-btn"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <ChatMessages messages={messages} isLoading={isLoading} endRef={messagesEndRef} />

          {showEndForm ? (
            <EndChatForm
              clientInfo={clientInfo}
              setClientInfo={setClientInfo}
              isLoading={isLoading}
              onSubmit={handleEndChat}
              onCancel={() => setShowEndForm(false)}
            />
          ) : (
            <ChatInput
              inputMessage={inputMessage}
              setInputMessage={setInputMessage}
              isLoading={isLoading}
              onSend={handleSend}
              onShowEndForm={() => setShowEndForm(true)}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ChatBot;
